/**
 * FacetRow Component (CCE-005)
 * 
 * Displays a single classification facet with:
 * - Label (left)
 * - Current value badge (center)
 * - Confidence badge (right)
 * - Dropdown for manual override (if not readOnly)
 * 
 * Accessibility (AC #8):
 * - Keyboard navigation (Tab, Enter, Arrow keys, Esc)
 * - Screen reader announcements
 * - ARIA labels
 * 
 * Created: February 3, 2026
 * Epic: CCE - Content Classification Enhancement
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ConfidenceBadge } from './ConfidenceBadge';
import { getFacetLabel } from '../../types/content-classification';

export interface FacetRowProps {
  facetName: string;
  facetValue: string;
  confidence: number; // 0.0-1.0
  possibleValues: Array<{ value: string; label: string }>;
  onOverride?: (newValue: string) => void;
  readOnly?: boolean;
}

export const FacetRow: React.FC<FacetRowProps> = ({
  facetName,
  facetValue,
  confidence,
  possibleValues,
  onOverride,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  const currentLabel = getFacetLabel(facetValue);
  const showDropdown = !readOnly && onOverride && possibleValues.length > 0;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen) {
          // Select highlighted item
          const selected = possibleValues[highlightedIndex];
          if (selected && onOverride) {
            onOverride(selected.value);
            setIsOpen(false);
            buttonRef.current?.focus();
          }
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => 
            prev < possibleValues.length - 1 ? prev + 1 : prev
          );
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(possibleValues.length - 1);
        }
        break;
    }
  };
  
  const handleSelect = (value: string) => {
    if (onOverride) {
      onOverride(value);
    }
    setIsOpen(false);
    buttonRef.current?.focus();
  };
  
  // Calculate confidence level for announcement
  const confidenceLevel = confidence >= 0.8 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  const confidencePercentage = Math.round(confidence * 100);
  
  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors">
      {/* Label (left) */}
      <div className="flex-1 min-w-0">
        <label 
          className="text-sm font-medium text-gray-700"
          id={`label-${facetName}`}
        >
          {facetName}
        </label>
      </div>
      
      {/* Value badge (center) */}
      <div className="flex-1 flex justify-center min-w-0 mx-4">
        <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
          {currentLabel}
        </span>
      </div>
      
      {/* Confidence badge (right) */}
      <div className="flex items-center space-x-3">
        <ConfidenceBadge confidence={confidence} size="sm" />
        
        {/* Dropdown button (if editable) */}
        {showDropdown && (
          <div className="relative" ref={dropdownRef}>
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={handleKeyDown}
              className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label={`${facetName}: ${currentLabel}, Confidence ${confidenceLevel} ${confidencePercentage}%, Change value`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {/* Dropdown menu */}
            {isOpen && (
              <div 
                className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-64 overflow-auto"
                role="listbox"
                aria-labelledby={`label-${facetName}`}
              >
                <div className="py-1">
                  {possibleValues.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={`
                        w-full text-left px-4 py-2 text-sm
                        ${index === highlightedIndex ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}
                        ${option.value === facetValue ? 'font-semibold bg-gray-100' : 'font-normal'}
                        hover:bg-blue-50 focus:bg-blue-100 focus:outline-none
                      `}
                      role="option"
                      aria-selected={option.value === facetValue}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {option.label}
                      {option.value === facetValue && (
                        <span className="ml-2 text-blue-600">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacetRow;
